// Phase I-A — Frontend mirror of /app/backend/project_normalize.py.
// READ-ONLY adapters for the legacy project Edit form. ZERO mutation of
// stored data — every adapter returns the canonical value PLUS the
// original raw value PLUS a status flag so the UI can render
// "Legacy: <value>" pills for orphan rows.
//
// Contract — every adapter returns:
//   { canonical, status, raw, warning }
//   • status: "empty" | "matched" | "legacy" | "ambiguous" | "ready_now"
//   • UI rule: if status === "legacy" → render the raw value as
//     `Legacy: <raw>` and DO NOT auto-pick the first dropdown option.

export const PROJECT_STATUS_CANONICAL = ["under_construction", "ready_to_move", "launching_soon"];

const PROJECT_STATUS_LABELS = {
  under_construction: "Under Construction",
  ready_to_move: "Ready to Move",
  launching_soon: "Launching Soon",
};

const PROJECT_STATUS_ALIASES = {
  ready: "ready_to_move",
  "ready to move": "ready_to_move",
  ready_to_move: "ready_to_move",
  "ready-to-move": "ready_to_move",
  rtm: "ready_to_move",
  completed: "ready_to_move",
  delivered: "ready_to_move",
  current: "ready_to_move",
  currently: "ready_to_move",
  "current ready": "ready_to_move",
  "under construction": "under_construction",
  under_construction: "under_construction",
  "under-construction": "under_construction",
  uc: "under_construction",
  "in progress": "under_construction",
  ongoing: "under_construction",
  launch: "launching_soon",
  launching: "launching_soon",
  launching_soon: "launching_soon",
  "launching soon": "launching_soon",
  "pre launch": "launching_soon",
  "pre-launch": "launching_soon",
  prelaunch: "launching_soon",
  upcoming: "launching_soon",
};

export function normalizeStatus(raw) {
  if (raw === null || raw === undefined) return { canonical: "", status: "empty", raw, warning: null };
  const s = String(raw).trim();
  if (!s) return { canonical: "", status: "empty", raw, warning: null };
  if (PROJECT_STATUS_CANONICAL.includes(s)) {
    return { canonical: s, status: "matched", raw, warning: null };
  }
  const canon = PROJECT_STATUS_ALIASES[s.toLowerCase()];
  if (canon) return { canonical: canon, status: "matched", raw, warning: null };
  return {
    canonical: s, status: "legacy", raw,
    warning: `Unknown status '${s}' — kept as legacy value.`,
  };
}

export function statusLabel(canonicalKey) {
  return PROJECT_STATUS_LABELS[canonicalKey] || canonicalKey || "";
}

const _BHK_RE = /^(\d{1,2})\s*[\-_ ]*bhk?$/i;

export function normalizeBhk(raw) {
  if (raw === null || raw === undefined) return { canonical: "", status: "empty", raw, warning: null };
  const s = String(raw).trim();
  if (!s) return { canonical: "", status: "empty", raw, warning: null };
  const low = s.toLowerCase();
  if (low.includes("studio")) return { canonical: "Studio", status: "matched", raw, warning: null };
  const m = low.match(_BHK_RE);
  if (m) {
    const n = parseInt(m[1], 10);
    if (n >= 1 && n <= 10) return { canonical: `${n} BHK`, status: "matched", raw, warning: null };
  }
  if (/^\d{1,2}$/.test(low)) {
    const n = parseInt(low, 10);
    if (n >= 1 && n <= 10) {
      return {
        canonical: `${n} BHK`, status: "matched", raw,
        warning: "Bare digit interpreted as BHK count.",
      };
    }
  }
  return { canonical: s, status: "legacy", raw, warning: `Unknown BHK '${s}' — kept as legacy value.` };
}

// Validate against the admin-managed Locations catalog.
// `locations` shape (from useConfig): [{ name, sub_areas: [{ name }] }].
export function normalizeMainArea(rawMain, locations) {
  if (rawMain === null || rawMain === undefined) return { canonical: "", status: "empty", raw: rawMain, warning: null };
  const s = String(rawMain).trim();
  if (!s) return { canonical: "", status: "empty", raw: rawMain, warning: null };
  const found = (locations || []).find((m) => String(m?.name || "").trim() === s);
  if (found) return { canonical: s, status: "matched", raw: rawMain, warning: null };
  return {
    canonical: s, status: "legacy", raw: rawMain,
    warning: `Main area '${s}' not in admin Locations catalog — kept as legacy value.`,
  };
}

export function normalizeSubArea(rawSub, rawMain, locations) {
  if (rawSub === null || rawSub === undefined) return { canonical: "", status: "empty", raw: rawSub, warning: null };
  const s = String(rawSub).trim();
  if (!s) return { canonical: "", status: "empty", raw: rawSub, warning: null };
  const main = String(rawMain || "").trim();
  const mainEntry = (locations || []).find((m) => String(m?.name || "").trim() === main);
  if (!mainEntry) {
    return { canonical: s, status: "legacy", raw: rawSub, warning: `Sub-area '${s}' has no matching main area — kept as legacy.` };
  }
  const sub = (mainEntry.sub_areas || []).find((x) => String(x?.name || "").trim() === s);
  if (sub) return { canonical: s, status: "matched", raw: rawSub, warning: null };
  return {
    canonical: s, status: "legacy", raw: rawSub,
    warning: `Sub-area '${s}' not in '${main}' — kept as legacy value.`,
  };
}

const _MONTH_MAP = {
  jan: "01", feb: "02", mar: "03", apr: "04", may: "05", jun: "06",
  jul: "07", aug: "08", sep: "09", sept: "09", oct: "10", nov: "11", dec: "12",
  january: "01", february: "02", march: "03", april: "04", june: "06",
  july: "07", august: "08", september: "09", october: "10", november: "11", december: "12",
};

const _READY_NOW_TOKENS = new Set([
  "ready", "ready to move", "ready_to_move", "rtm", "current", "currently",
  "available now", "available", "completed", "delivered", "now", "immediate",
  "immediately", "ready now",
]);

export function normalizePossession(raw) {
  if (raw === null || raw === undefined) return { canonical: "", status: "empty", raw, warning: null };
  const s = String(raw).trim();
  if (!s) return { canonical: "", status: "empty", raw, warning: null };
  const low = s.toLowerCase();
  if (_READY_NOW_TOKENS.has(low)) return { canonical: "", status: "ready_now", raw, warning: null };
  let m = s.match(/^(\d{4})-(\d{1,2})$/);
  if (m) {
    const y = parseInt(m[1], 10), mo = parseInt(m[2], 10);
    if (mo >= 1 && mo <= 12 && y >= 1900 && y <= 2100) {
      return { canonical: `${m[1]}-${String(mo).padStart(2, "0")}`, status: "matched", raw, warning: null };
    }
    return { canonical: s, status: "legacy", raw, warning: `Invalid month '${s}'.` };
  }
  m = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (m) {
    const y = parseInt(m[1], 10), mo = parseInt(m[2], 10);
    if (mo >= 1 && mo <= 12 && y >= 1900 && y <= 2100) {
      return {
        canonical: `${m[1]}-${String(mo).padStart(2, "0")}`, status: "matched", raw,
        warning: "Day component dropped (canonical = month).",
      };
    }
  }
  m = s.match(/^([A-Za-z]+)\s*[\-/ ]?\s*(\d{4})$/);
  if (m) {
    const mo = _MONTH_MAP[m[1].toLowerCase()];
    const y = parseInt(m[2], 10);
    if (mo && y >= 1900 && y <= 2100) {
      return { canonical: `${m[2]}-${mo}`, status: "matched", raw, warning: null };
    }
  }
  m = s.match(/^(\d{4})\s*[\-/ ]?\s*([A-Za-z]+)$/);
  if (m) {
    const mo = _MONTH_MAP[m[2].toLowerCase()];
    const y = parseInt(m[1], 10);
    if (mo && y >= 1900 && y <= 2100) {
      return { canonical: `${m[1]}-${mo}`, status: "matched", raw, warning: null };
    }
  }
  return { canonical: s, status: "legacy", raw, warning: `Unrecognized possession '${s}' — kept as legacy value.` };
}

// Extracts the project's array of legacy-status orphan tokens so the
// Edit form can render them as `<SelectItem value="legacy:<raw>">Legacy: <raw></SelectItem>`
// alongside the canonical options. Adapter is pure — no DOM, no state.
export function legacyTokenFor(raw) {
  if (raw === null || raw === undefined || String(raw).trim() === "") return "";
  return `__legacy__::${String(raw)}`;
}
export const LEGACY_PREFIX = "__legacy__::";
export function isLegacyToken(v) {
  return typeof v === "string" && v.startsWith(LEGACY_PREFIX);
}
export function legacyTokenRaw(v) {
  return isLegacyToken(v) ? v.slice(LEGACY_PREFIX.length) : "";
}
